import React, { useState } from 'react';
import { ArrowLeftIcon, PencilIcon, CheckCircleIcon, ClockIcon, XCircleIcon, MapPinIcon, FileTextIcon, UsersIcon, ScaleIcon, HistoryIcon, MapIcon, AlertTriangleIcon, PlusIcon, CalendarIcon, BuildingIcon, LandmarkIcon, CoinsIcon, LinkIcon, CompassIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { LandRecord, VerificationStatus, RecordStatus } from '../data/types';
type TabId = 'primary' | 'deed' | 'area' | 'value' | 'parties' | 'poa' | 'ownership' | 'schedule' | 'periphery' | 'location' | 'mutation' | 'khajna' | 'transfer' | 'litigation' | 'documents' | 'activity';
interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}
const tabs: Tab[] = [{
  id: 'primary',
  label: 'Primary Info',
  icon: <FileTextIcon className="w-4 h-4" data-id="element-505" />
}, {
  id: 'deed',
  label: 'Deed Details',
  icon: <LandmarkIcon className="w-4 h-4" data-id="element-506" />
}, {
  id: 'area',
  label: 'Area & Type',
  icon: <MapIcon className="w-4 h-4" data-id="element-507" />
}, {
  id: 'value',
  label: 'Value & Costs',
  icon: <CoinsIcon className="w-4 h-4" data-id="element-508" />
}, {
  id: 'parties',
  label: 'Parties',
  icon: <UsersIcon className="w-4 h-4" data-id="element-509" />
}, {
  id: 'poa',
  label: 'Power of Attorney',
  icon: <LinkIcon className="w-4 h-4" data-id="element-510" />
}, {
  id: 'ownership',
  label: 'Ownership Chain',
  icon: <HistoryIcon className="w-4 h-4" data-id="element-511" />
}, {
  id: 'schedule',
  label: 'Schedule of Land',
  icon: <FileTextIcon className="w-4 h-4" data-id="element-512" />
}, {
  id: 'periphery',
  label: 'Periphery',
  icon: <CompassIcon className="w-4 h-4" data-id="element-513" />
}, {
  id: 'location',
  label: 'Location',
  icon: <MapPinIcon className="w-4 h-4" data-id="element-514" />
}, {
  id: 'mutation',
  label: 'Mutation',
  icon: <FileTextIcon className="w-4 h-4" data-id="element-515" />
}, {
  id: 'khajna',
  label: 'Khajna',
  icon: <CoinsIcon className="w-4 h-4" data-id="element-516" />
}, {
  id: 'transfer',
  label: 'Subsequent Transfer',
  icon: <LinkIcon className="w-4 h-4" data-id="element-517" />
}, {
  id: 'litigation',
  label: 'Litigation',
  icon: <ScaleIcon className="w-4 h-4" data-id="element-518" />
}, {
  id: 'documents',
  label: 'Documents',
  icon: <FileTextIcon className="w-4 h-4" data-id="element-519" />
}, {
  id: 'activity',
  label: 'Activity Log',
  icon: <HistoryIcon className="w-4 h-4" data-id="element-520" />
}];
export function LandRecordDetails() {
  const {
    state,
    dispatch,
    navigate,
    canEdit
  } = useApp();
  const {
    landRecords,
    selectedRecordId,
    language
  } = state;
  const [activeTab, setActiveTab] = useState<TabId>('primary');
  const [showAddLitigation, setShowAddLitigation] = useState(false);
  const record = landRecords.find(r => r.id === selectedRecordId);
  if (!record) {
    return <div className="p-6 text-center" data-id="element-521">
        <p className="text-gray-500" data-id="element-522">Record not found</p>
        <button onClick={() => navigate('land-records')} className="mt-4 text-blue-600 hover:text-blue-700" data-id="element-523">
          Back to Land Records
        </button>
      </div>;
  }
  const getStatusBadge = (status: RecordStatus) => {
    if (status === 'Active') {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700" data-id="element-524">
          <CheckCircleIcon className="w-4 h-4" data-id="element-525" />
          Active
        </span>;
    }
    return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600" data-id="element-526">
        Inactive
      </span>;
  };
  const getVerificationBadge = (verification: VerificationStatus) => {
    switch (verification) {
      case 'Verified':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700" data-id="element-527">
            <CheckCircleIcon className="w-4 h-4" data-id="element-528" />
            Verified
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700" data-id="element-529">
            <ClockIcon className="w-4 h-4" data-id="element-530" />
            Pending
          </span>;
      case 'Rejected':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700" data-id="element-531">
            <XCircleIcon className="w-4 h-4" data-id="element-532" />
            Rejected
          </span>;
    }
  };
  const handleAddLitigation = () => {
    const newLitigation = {
      id: `lit-${Date.now()}`,
      caseNo: 'CS-2026-NEW',
      type: 'Land Dispute',
      plaintiff: 'Demo Plaintiff',
      defendant: 'Incepta Pharmaceuticals Ltd.',
      court: 'District Judge Court, Dhaka',
      status: 'Ongoing' as const
    };
    dispatch({
      type: 'ADD_LITIGATION',
      payload: {
        recordId: record.id,
        litigation: newLitigation
      }
    });
    setShowAddLitigation(false);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'currency',
      currency: 'BDT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'primary':
        return <div className="space-y-6" data-id="element-533">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-534">
              <InfoField label="Land ID" value={record.landId} data-id="element-535" />
              <InfoField label="Land Name" value={record.landName} data-id="element-536" />
              <InfoField label="Status" value={getStatusBadge(record.status)} data-id="element-537" />
              <InfoField label="Verification" value={getVerificationBadge(record.verification)} data-id="element-538" />
              <InfoField label="Created By" value={record.createdBy} data-id="element-539" />
              <InfoField label="Created Date" value={record.createdDate} data-id="element-540" />
              <InfoField label="Last Updated By" value={record.lastUpdatedBy} data-id="element-541" />
              <InfoField label="Last Updated" value={record.lastUpdatedDate} data-id="element-542" />
            </div>
          </div>;
      case 'deed':
        return <div className="space-y-6" data-id="element-543">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-544">
              <InfoField label="Deed No" value={record.deedNo} mono data-id="element-545" />
              <InfoField label="Deed Type" value={record.deedType} data-id="element-546" />
              <InfoField label="Execution Date" value={record.executionDate} data-id="element-547" />
              <InfoField label="Registration Date" value={record.registrationDate} data-id="element-548" />
              <InfoField label="Sub-Registry Office" value={record.subRegistry} data-id="element-549" />
              <InfoField label="Mouza" value={record.mouza} data-id="element-550" />
              <InfoField label="Union/Ward" value={record.union} data-id="element-551" />
              <InfoField label="Thana/Upazila" value={record.upazila} data-id="element-552" />
              <InfoField label="District" value={record.district} data-id="element-553" />
            </div>
          </div>;
      case 'area':
        return <div className="space-y-6" data-id="element-554">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-555">
              <InfoField label="Area (Decimal)" value={`${record.areaDecimal} Decimal`} data-id="element-556" />
              <InfoField label="Area (Katha)" value={record.areaKatha ? `${record.areaKatha} Katha` : '-'} data-id="element-557" />
              <InfoField label="Area (Acre)" value={record.areaAcre ? `${record.areaAcre} Acre` : '-'} data-id="element-558" />
              <InfoField label="Land Type" value={record.landType} data-id="element-559" />
            </div>
          </div>;
      case 'value':
        return <div className="space-y-6" data-id="element-560">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-561">
              {record.sapRef && <InfoField label="SAP Reference" value={record.sapRef} mono data-id="element-562" />}
              <InfoField label="Actual Value" value={formatCurrency(record.actualValue)} data-id="element-563" />
              <InfoField label="Deed Value" value={formatCurrency(record.deedValue)} data-id="element-564" />
              <InfoField label="Mouza Value" value={formatCurrency(record.mouzaValue)} data-id="element-565" />
            </div>
            <div className="border-t border-gray-200 pt-6" data-id="element-566">
              <h4 className="font-medium text-gray-900 mb-4" data-id="element-567">
                Registration Costs
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-568">
                <InfoField label="Stamp Duty" value={formatCurrency(record.registrationCosts.stampDuty)} data-id="element-569" />
                <InfoField label="Registration Fee" value={formatCurrency(record.registrationCosts.registrationFee)} data-id="element-570" />
                <InfoField label="Local Govt Tax" value={formatCurrency(record.registrationCosts.localGovtTax)} data-id="element-571" />
                <InfoField label="E-Stamp" value={formatCurrency(record.registrationCosts.eStamp)} data-id="element-572" />
                {record.registrationCosts.miscellaneous && <InfoField label="Miscellaneous" value={formatCurrency(record.registrationCosts.miscellaneous)} data-id="element-573" />}
                <div className="md:col-span-2 pt-4 border-t border-gray-100" data-id="element-574">
                  <InfoField label="Total Registration Cost" value={formatCurrency(record.registrationCosts.total)} highlight data-id="element-575" />
                </div>
              </div>
            </div>
          </div>;
      case 'parties':
        return <div className="space-y-6" data-id="element-576">
            <div data-id="element-577">
              <h4 className="font-medium text-gray-900 mb-4" data-id="element-578">Transferor(s)</h4>
              <div className="space-y-4" data-id="element-579">
                {record.transferors.map((party, index) => <div key={index} className="bg-gray-50 rounded-lg p-4" data-id="element-580">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-581">
                      <InfoField label="Name" value={party.name} data-id="element-582" />
                      <InfoField label="NID" value={party.nid} mono data-id="element-583" />
                      <div className="md:col-span-2" data-id="element-584">
                        <InfoField label="Address" value={party.address} data-id="element-585" />
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div data-id="element-586">
              <h4 className="font-medium text-gray-900 mb-4" data-id="element-587">Transferee(s)</h4>
              <div className="space-y-4" data-id="element-588">
                {record.transferees.map((party, index) => <div key={index} className="bg-gray-50 rounded-lg p-4" data-id="element-589">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-590">
                      <InfoField label="Name" value={party.name} data-id="element-591" />
                      <InfoField label="NID/TIN" value={party.nid} mono data-id="element-592" />
                      {party.representative && <InfoField label="Representative" value={party.representative} data-id="element-593" />}
                      <div className="md:col-span-2" data-id="element-594">
                        <InfoField label="Address" value={party.address} data-id="element-595" />
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'poa':
        return <div className="space-y-6" data-id="element-596">
            {record.poa.enabled ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-597">
                <InfoField label="POA Deed No" value={record.poa.deedNo || '-'} mono data-id="element-598" />
                <InfoField label="Execution Date" value={record.poa.executionDate || '-'} data-id="element-599" />
                <InfoField label="Principal" value={record.poa.principal || '-'} data-id="element-600" />
                <InfoField label="Attorney" value={record.poa.attorney || '-'} data-id="element-601" />
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-602">
                <LinkIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-603" />
                <p data-id="element-604">No Power of Attorney for this record</p>
              </div>}
          </div>;
      case 'ownership':
        return <div className="space-y-4" data-id="element-605">
            {!record.ownershipChainComplete && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-606">
                <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-607" />
                <div data-id="element-608">
                  <p className="font-medium text-amber-800" data-id="element-609">
                    Incomplete Ownership Chain
                  </p>
                  <p className="text-sm text-amber-700" data-id="element-610">
                    25-year ownership chain is required for complete
                    verification
                  </p>
                </div>
              </div>}
            {record.ownershipChain.length > 0 ? <div className="space-y-4" data-id="element-611">
                {record.ownershipChain.map((entry, index) => <div key={entry.id} className="relative pl-8 pb-4" data-id="element-612">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-613" />
                    <div className="absolute left-1.5 top-1.5 w-4 h-4 rounded-full bg-blue-600 border-2 border-white" data-id="element-614" />
                    <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-615">
                      <div className="flex items-center justify-between mb-2" data-id="element-616">
                        <span className="font-mono text-sm font-medium text-blue-600" data-id="element-617">
                          {entry.deedNo}
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-618">
                          {entry.registrationDate}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-619">
                        {entry.deedType}
                      </p>
                      <div className="text-sm" data-id="element-620">
                        <span className="text-gray-500" data-id="element-621">From:</span>{' '}
                        <span className="text-gray-900" data-id="element-622">
                          {entry.transferor}
                        </span>
                      </div>
                      <div className="text-sm" data-id="element-623">
                        <span className="text-gray-500" data-id="element-624">To:</span>{' '}
                        <span className="text-gray-900" data-id="element-625">
                          {entry.transferee}
                        </span>
                      </div>
                      {entry.history && <p className="text-xs text-gray-500 mt-2 italic" data-id="element-626">
                          {entry.history}
                        </p>}
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-627">
                <HistoryIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-628" />
                <p data-id="element-629">No ownership chain records</p>
              </div>}
          </div>;
      case 'schedule':
        return <div className="space-y-6" data-id="element-630">
            {['cs', 'sa', 'rs', 'bds'].map(section => {
            const data = record.scheduleOfLand[section as keyof typeof record.scheduleOfLand];
            if (!data) return null;
            return <div key={section} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-631">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-632">
                    <h4 className="font-medium text-gray-900" data-id="element-633">
                      {section.toUpperCase()} Record
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-634">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-635">
                      <InfoField label="Khatian No" value={data.khatianNo} mono data-id="element-636" />
                      <InfoField label="Total Land" value={data.totalLand} data-id="element-637" />
                    </div>
                    <div className="border-t border-gray-100 pt-4" data-id="element-638">
                      <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-639">
                        Dag Details
                      </p>
                      <div className="overflow-x-auto" data-id="element-640">
                        <table className="w-full text-sm" data-id="element-641">
                          <thead data-id="element-642">
                            <tr className="text-left text-gray-500" data-id="element-643">
                              <th className="pb-2" data-id="element-644">Dag No</th>
                              <th className="pb-2" data-id="element-645">Shared Land</th>
                              <th className="pb-2" data-id="element-646">Transferred Land</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-647">
                            {data.dags.map(dag => <tr key={dag.id} className="border-t border-gray-100" data-id="element-648">
                                <td className="py-2 font-mono" data-id="element-649">{dag.dagNo}</td>
                                <td className="py-2" data-id="element-650">{dag.sharedLand}</td>
                                <td className="py-2" data-id="element-651">{dag.transferredLand}</td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>;
      case 'periphery':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-652">
            <InfoField label="North" value={record.periphery.north} data-id="element-653" />
            <InfoField label="South" value={record.periphery.south} data-id="element-654" />
            <InfoField label="East" value={record.periphery.east} data-id="element-655" />
            <InfoField label="West" value={record.periphery.west} data-id="element-656" />
          </div>;
      case 'location':
        return <div className="space-y-6" data-id="element-657">
            {record.location ? <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-658">
                  <InfoField label="Latitude" value={record.location.lat.toString()} mono data-id="element-659" />
                  <InfoField label="Longitude" value={record.location.lng.toString()} mono data-id="element-660" />
                  <InfoField label="Label" value={record.location.label} data-id="element-661" />
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center relative overflow-hidden" data-id="element-662">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-green-50" data-id="element-663" />
                  <div className="relative text-center" data-id="element-664">
                    <MapPinIcon className="w-12 h-12 text-red-500 mx-auto mb-2" data-id="element-665" />
                    <p className="font-medium text-gray-900" data-id="element-666">
                      {record.location.label}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-667">
                      {record.location.lat}, {record.location.lng}
                    </p>
                  </div>
                </div>
              </> : <div className="text-center py-8 text-gray-500" data-id="element-668">
                <MapPinIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-669" />
                <p data-id="element-670">No location data available</p>
              </div>}
          </div>;
      case 'mutation':
        return <div className="space-y-6" data-id="element-671">
            {record.mutation ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-672">
                <InfoField label="Case No" value={record.mutation.caseNo} mono data-id="element-673" />
                <InfoField label="Filing Date" value={record.mutation.filingDate} data-id="element-674" />
                <InfoField label="Jote No" value={record.mutation.joteNo} data-id="element-675" />
                <InfoField label="Mutation Khatian No" value={record.mutation.mutationKhatianNo} data-id="element-676" />
                <InfoField label="Total Land" value={record.mutation.totalLand} data-id="element-677" />
                <InfoField label="File No" value={record.mutation.fileNo} mono data-id="element-678" />
                <InfoField label="Status" value={<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${record.mutation.status === 'Approved' ? 'bg-green-100 text-green-700' : record.mutation.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-680">
                      {record.mutation.status}
                    </span>} data-id="element-679" />
                {record.mutation.followUpDue && <InfoField label="Follow-up Due" value={<span className="flex items-center gap-2" data-id="element-682">
                        <CalendarIcon className="w-4 h-4 text-amber-500" data-id="element-683" />
                        {record.mutation.followUpDue}
                      </span>} data-id="element-681" />}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-684">
                <FileTextIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-685" />
                <p data-id="element-686">No mutation data available</p>
              </div>}
          </div>;
      case 'khajna':
        return <div className="space-y-6" data-id="element-687">
            {record.khajna ? <>
                {!record.khajna.upToDate && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-id="element-688">
                    <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-689" />
                    <div data-id="element-690">
                      <p className="font-medium text-red-800" data-id="element-691">
                        Khajna Payment Due
                      </p>
                      <p className="text-sm text-red-700" data-id="element-692">
                        Due date: {record.khajna.dueDate}
                      </p>
                    </div>
                  </div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-693">
                  <InfoField label="Receipt No" value={record.khajna.receiptNo} mono data-id="element-694" />
                  <InfoField label="Case No" value={record.khajna.caseNo} mono data-id="element-695" />
                  <InfoField label="Submission Date" value={record.khajna.submissionDate} data-id="element-696" />
                  <InfoField label="Due Date" value={record.khajna.dueDate} data-id="element-697" />
                  <InfoField label="Total Land" value={record.khajna.totalLand} data-id="element-698" />
                  <InfoField label="File No" value={record.khajna.fileNo} mono data-id="element-699" />
                  <InfoField label="Up to Date" value={record.khajna.upToDate ? <span className="inline-flex items-center gap-1 text-green-600" data-id="element-701">
                          <CheckCircleIcon className="w-4 h-4" data-id="element-702" /> Yes
                        </span> : <span className="inline-flex items-center gap-1 text-red-600" data-id="element-703">
                          <XCircleIcon className="w-4 h-4" data-id="element-704" /> No
                        </span>} data-id="element-700" />
                </div>
              </> : <div className="text-center py-8 text-gray-500" data-id="element-705">
                <CoinsIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-706" />
                <p data-id="element-707">No khajna data available</p>
              </div>}
          </div>;
      case 'transfer':
        return <div className="space-y-6" data-id="element-708">
            {record.subsequentTransfer.enabled ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-709">
                <InfoField label="Transfer Type" value={record.subsequentTransfer.type || '-'} data-id="element-710" />
                <InfoField label="Transferee" value={record.subsequentTransfer.transferee || '-'} data-id="element-711" />
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-712">
                <LinkIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-713" />
                <p data-id="element-714">No subsequent transfer</p>
              </div>}
          </div>;
      case 'litigation':
        return <div className="space-y-4" data-id="element-715">
            {canEdit() && <div className="flex justify-end" data-id="element-716">
                <button onClick={() => setShowAddLitigation(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm" data-id="element-717">
                  <PlusIcon className="w-4 h-4" data-id="element-718" />
                  Add Litigation
                </button>
              </div>}
            {showAddLitigation && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-719">
                <p className="text-sm text-blue-800 mb-3" data-id="element-720">
                  Demo: Click to add a sample litigation case
                </p>
                <div className="flex gap-2" data-id="element-721">
                  <button onClick={handleAddLitigation} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-722">
                    Add Demo Litigation
                  </button>
                  <button onClick={() => setShowAddLitigation(false)} className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-id="element-723">
                    Cancel
                  </button>
                </div>
              </div>}
            {record.litigation.length > 0 ? <div className="space-y-4" data-id="element-724">
                {record.litigation.map(lit => <div key={lit.id} className="border border-gray-200 rounded-lg p-4" data-id="element-725">
                    <div className="flex items-start justify-between mb-4" data-id="element-726">
                      <div data-id="element-727">
                        <span className="font-mono text-sm font-medium text-gray-900" data-id="element-728">
                          {lit.caseNo}
                        </span>
                        <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${lit.status === 'Ongoing' ? 'bg-amber-100 text-amber-700' : lit.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-729">
                          {lit.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-730">
                      <div data-id="element-731">
                        <span className="text-gray-500" data-id="element-732">Type:</span>{' '}
                        <span className="text-gray-900" data-id="element-733">{lit.type}</span>
                      </div>
                      <div data-id="element-734">
                        <span className="text-gray-500" data-id="element-735">Court:</span>{' '}
                        <span className="text-gray-900" data-id="element-736">{lit.court}</span>
                      </div>
                      <div data-id="element-737">
                        <span className="text-gray-500" data-id="element-738">Plaintiff:</span>{' '}
                        <span className="text-gray-900" data-id="element-739">{lit.plaintiff}</span>
                      </div>
                      <div data-id="element-740">
                        <span className="text-gray-500" data-id="element-741">Defendant:</span>{' '}
                        <span className="text-gray-900" data-id="element-742">{lit.defendant}</span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-743">
                <ScaleIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-744" />
                <p data-id="element-745">No litigation cases</p>
              </div>}
          </div>;
      case 'documents':
        return <div className="space-y-4" data-id="element-746">
            {record.documents.length > 0 ? <div className="space-y-3" data-id="element-747">
                {record.documents.map(doc => <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-748">
                    <div className="flex items-center gap-3" data-id="element-749">
                      <div className="p-2 bg-white rounded-lg border border-gray-200" data-id="element-750">
                        <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-751" />
                      </div>
                      <div data-id="element-752">
                        <p className="font-medium text-gray-900" data-id="element-753">{doc.title}</p>
                        <p className="text-sm text-gray-500" data-id="element-754">
                          {doc.type} • {doc.date}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-755">
                      {doc.tags.map(tag => <span key={tag} className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs" data-id="element-756">
                          {tag}
                        </span>)}
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-id="element-757">
                        View
                      </button>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-758">
                <FileTextIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-759" />
                <p data-id="element-760">No documents attached</p>
              </div>}
          </div>;
      case 'activity':
        return <div className="space-y-4" data-id="element-761">
            {record.activityLog.length > 0 ? <div className="space-y-4" data-id="element-762">
                {record.activityLog.map((entry, index) => <div key={entry.id} className="relative pl-8" data-id="element-763">
                    {index < record.activityLog.length - 1 && <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200" data-id="element-764" />}
                    <div className="absolute left-1.5 top-1.5 w-4 h-4 rounded-full bg-blue-100 border-2 border-blue-500" data-id="element-765" />
                    <div className="pb-4" data-id="element-766">
                      <div className="flex items-center gap-2 mb-1" data-id="element-767">
                        <span className="font-medium text-gray-900" data-id="element-768">
                          {entry.action}
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-769">
                          by {entry.user}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-770">{entry.timestamp}</p>
                      {entry.details && <p className="text-sm text-gray-600 mt-1" data-id="element-771">
                          {entry.details}
                        </p>}
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500" data-id="element-772">
                <HistoryIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-773" />
                <p data-id="element-774">No activity recorded</p>
              </div>}
          </div>;
      default:
        return null;
    }
  };
  return <div className="p-4 lg:p-6 space-y-4" data-id="element-775">
      {/* Back Button & Header */}
      <div className="flex items-center gap-4" data-id="element-776">
        <button onClick={() => navigate('land-records')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-777">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-778" />
        </button>
        <div className="flex-1" data-id="element-779">
          <div className="flex items-center gap-3 flex-wrap" data-id="element-780">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-id="element-781">
              {record.landId}
            </h1>
            {getStatusBadge(record.status)}
            {getVerificationBadge(record.verification)}
          </div>
          <p className="text-gray-600 mt-1" data-id="element-782">{record.landName}</p>
        </div>
        {canEdit() && <button onClick={() => navigate('land-record-edit', record.id)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-783">
            <PencilIcon className="w-4 h-4" data-id="element-784" />
            <span className="hidden sm:inline" data-id="element-785">Edit</span>
          </button>}
      </div>

      {/* Tabs & Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-786">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 overflow-x-auto" data-id="element-787">
          <div className="flex min-w-max" data-id="element-788">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'}`} data-id="element-789">
                {tab.icon}
                <span className="hidden lg:inline" data-id="element-790">{tab.label}</span>
              </button>)}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6" data-id="element-791">{renderTabContent()}</div>
      </div>
    </div>;
}
// Helper component for displaying info fields
function InfoField({
  label,
  value,
  mono = false,
  highlight = false
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
}) {
  return <div data-id="element-792">
      <p className="text-sm text-gray-500 mb-1" data-id="element-793">{label}</p>
      <div className={`${mono ? 'font-mono' : ''} ${highlight ? 'text-lg font-semibold text-blue-600' : 'text-gray-900'}`} data-id="element-794">
        {value}
      </div>
    </div>;
}